(() => {
  if (window.BaechleinPrivacy) return;
  const purposes = ['preferences', 'analytics', 'marketing'];
  const methods = { preferences: 'preferencesProcessingAllowed', analytics: 'analyticsProcessingAllowed', marketing: 'marketingAllowed' };
  const pending = new Set();
  const started = new Set();
  let loading;
  let previous = {};
  let returnFocus;
  let nativeWatcher;
  const DISMISSED_KEY = 'b-privacy-dismissed';
  const api = () => window.Shopify?.customerPrivacy;
  // Require an affirmative choice even outside Shopify's consent-required regions.
  const allowed = (purpose) => {
    try {
      return api()?.currentVisitorConsent?.()[purpose] === 'yes' && api()?.[methods[purpose]]?.() === true;
    } catch { return false; }
  };
  const panel = () => document.getElementById('BPrivacyPanel');
  const nativeVisible = () => [...document.querySelectorAll('#shopify-pc__banner, #shopify-pc__prefs__dialog')]
    .some(element => element.getClientRects().length > 0);
  const refresh = () => {
    const state = Object.fromEntries(purposes.map(p => [p, allowed(p)]));
    window.gtag?.('consent', 'update', {
      analytics_storage: state.analytics ? 'granted' : 'denied',
      ad_storage: state.marketing ? 'granted' : 'denied',
      ad_user_data: state.marketing ? 'granted' : 'denied',
      ad_personalization: state.marketing ? 'granted' : 'denied'
    });
    document.documentElement.toggleAttribute('data-b-marketing-consent', state.marketing);
    const revoked = purposes.some(p => previous[p] && !state[p] && started.has(p));
    previous = state;
    // Third-party runtimes cannot be unloaded reliably. Reload after the saved withdrawal.
    if (revoked) { window.location.reload(); return; }
    for (const job of pending) {
      if (!state[job.purpose]) continue;
      pending.delete(job);
      started.add(job.purpose);
      job.callback();
    }
  };
  const ready = () => {
    if (api()) return Promise.resolve(api());
    if (loading) return loading;
    loading = new Promise((resolve, reject) => {
      const timeout = setTimeout(() => reject(new Error('Privacy API unavailable')), 10000);
      if (!window.Shopify?.loadFeatures) { clearTimeout(timeout); reject(new Error('Privacy API unavailable')); return; }
      window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], error => {
        clearTimeout(timeout);
        if (error || !api()) reject(new Error('Privacy API unavailable'));
        else resolve(api());
      });
    }).catch(error => { loading = undefined; throw error; });
    return loading;
  };
  const whenAllowed = (purpose, callback) => {
    const job = { purpose, callback };
    pending.add(job);
    if (allowed(purpose)) refresh();
    return () => pending.delete(job);
  };
  const hidePanel = () => {
    const element = panel();
    if (element) element.hidden = true;
    nativeWatcher?.disconnect();
    nativeWatcher = undefined;
    returnFocus?.focus();
    returnFocus = undefined;
  };
  const showPanel = (focus = false) => {
    const element = panel();
    if (!element) return;
    purposes.forEach(p => { element.querySelector(`[name="${p}"]`).checked = allowed(p); });
    element.querySelector('[data-privacy-error]').hidden = true;
    element.hidden = false;
    if (focus) { returnFocus = document.activeElement; element.querySelector('h2').focus(); }
    // Shopify's banner takes over when it turns up; watch only while ours shows.
    if (!nativeWatcher) {
      nativeWatcher = new MutationObserver(() => { if (nativeVisible()) hidePanel(); });
      nativeWatcher.observe(document.body, { childList: true, subtree: true });
    }
  };
  const dismissed = () => { try { return sessionStorage.getItem(DISMISSED_KEY) === '1'; } catch { return false; } };
  const dismissForSession = () => { try { sessionStorage.setItem(DISMISSED_KEY, '1'); } catch { /* it just closes */ } };
  const showPreferences = async () => {
    // Use the configured Shopify UI when available, with a local fallback on password pages.
    try {
      if (window.privacyBanner?.showPreferences) {
        await window.privacyBanner.showPreferences();
        if (nativeVisible()) return;
      }
    } catch { /* The fallback still uses Shopify's API to save the choice. */ }
    showPanel(true);
  };
  const save = async (choice) => {
    const privacy = await ready();
    await new Promise((resolve, reject) => {
      const timeout = setTimeout(() => reject(new Error('Consent could not be saved')), 10000);
      privacy.setTrackingConsent(choice, result => {
        clearTimeout(timeout);
        if (result?.error) reject(new Error('Consent could not be saved'));
        else resolve();
      });
    });
    refresh();
  };
  window.BaechleinPrivacy = { allowed, ready, whenAllowed, showPreferences, save,
    waitFor: purpose => new Promise(resolve => whenAllowed(purpose, resolve)) };
  document.addEventListener('visitorConsentCollected', () => {
    refresh();
    hidePanel();
  });
  document.addEventListener('click', async event => {
    if (event.target.closest('[data-privacy-open]')) { event.preventDefault(); showPreferences(); return; }
    const button = event.target.closest('[data-privacy-save]');
    if (!button) return;
    const element = panel();
    const buttons = element.querySelectorAll('button');
    buttons.forEach(b => { b.disabled = true; });
    element.querySelector('[data-privacy-error]').hidden = true;
    const choice = Object.fromEntries(purposes.map(p => [p,
      button.dataset.privacySave === 'selection' ? element.querySelector(`[name="${p}"]`).checked : button.dataset.privacySave === 'accept'
    ]));
    try { await save(choice); hidePanel(); }
    catch {
      // Declining leaves optional content blocked whether or not Shopify stored
      // it, so a failed save must not trap the visitor behind the panel.
      if (button.dataset.privacySave === 'decline') { dismissForSession(); hidePanel(); }
      else element.querySelector('[data-privacy-error]').hidden = false;
    }
    finally { buttons.forEach(b => { b.disabled = false; }); }
  });
  const unanswered = () => {
    try { return !purposes.some(p => ['yes', 'no'].includes(api()?.currentVisitorConsent?.()[p])); }
    catch { return true; }
  };
  // Safety net for pages without a banner. Shopify's banner loads late, so give
  // the page time before deciding none is coming.
  const offerFallback = () => {
    const decide = () => setTimeout(() => {
      if (unanswered() && !dismissed() &&
          !document.querySelector('cookie-banner, #shopify-pc__banner, #shopify-pc__prefs__dialog')) showPanel();
    }, 1500);
    if (document.readyState === 'complete') decide();
    else window.addEventListener('load', decide, { once: true });
  };
  const init = async () => {
    try {
      await ready();
      refresh();
    } catch { /* No consent API: theme integrations stay blocked. */ }
    if (!window.Shopify?.designMode) offerFallback();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
